import { useEffect, useMemo, useRef, useState } from "react";
import {Container, Row, Form, Nav, Alert, Spinner,} from "react-bootstrap";
import SatAPI from "../../_services/sat";
import toastService from "../../_services/toastService";
import OlaTable from "./OlaTable";
import PhaseTable from "./PhaseTable";
import TaskTables from "./TaskTables";

const api = SatAPI();

function HoewistResults() {
  const [collegas, setCollegas] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [academicYears, setAcademicYears] = useState([]);

  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);

  const [programmes, setProgrammes] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [olasByProgramme, setOlasByProgramme] = useState({});
  const [phasesByProgramme, setPhasesByProgramme] = useState({});
  const [currentTaskPrefs, setCurrentTaskPrefs] = useState([]);
  const [newTaskPrefs, setNewTaskPrefs] = useState([]);
  const [thresholdSuccess, setThresholdSuccess] = useState(7);
  const [thresholdWarning, setThresholdWarning] = useState(4);
  const teacherDataRef = useRef({});

  const scoreVariant = score => {
    if (score >= thresholdSuccess) return "success";
    if (score >= thresholdWarning) return "warning";
    return "danger";
  };

  const isTaskTab = activeTab === "taken";

  useEffect(() => { loadInitial(); }, []);

  const loadInitial = async () => {
    try {
      const [collegasData, tasksData, globalSettings, academicYearsData] = await Promise.all([
        api.getCollegas(),
        api.getTasks().catch(() => []),
        api.getGlobalSettings().catch(() => null),
        api.getAcademicYears().catch(() => []),
      ]);

      const sorted = (collegasData || []).sort((a, b) =>
        a.lastName.localeCompare(b.lastName, "nl") ||
        a.firstName.localeCompare(b.firstName, "nl")
      );
      setCollegas(sorted);
      setAllTasks(tasksData || []);
      setAcademicYears((academicYearsData || []).sort((a, b) => b.year.localeCompare(a.year)));
      if (globalSettings?.preferenceScoreHigh != null) setThresholdSuccess(globalSettings.preferenceScoreHigh);
      if (globalSettings?.preferenceScoreLow != null) setThresholdWarning(globalSettings.preferenceScoreLow);
    } catch (error) {
      toastService.send({ title: "Fout bij laden", message: error.toString() });
    }
    setInitialLoading(false);
  };

  const applyPreferences = (sub, assignments, taskAssignments, fullOLAMap, olaPrefs, taskPrefs, phasePrefs, teacherProgrammeIdSet) => {
    const olaPrefMap = {};
    (olaPrefs || []).forEach(p => { olaPrefMap[p.olaId] = p; });

    const taskPrefMap = {};
    (taskPrefs || []).forEach(p => { taskPrefMap[p.taskId] = p; });

    const phasePrefMap = {};
    (phasePrefs || []).forEach(p => { phasePrefMap[p.phaseId] = p; });

    const seenOlaIds = new Set();
    const progMap = {};

    (assignments || []).forEach(a => {
      const ola = a.ola;
      if (!ola) return;
      const programme = ola.phases?.[0]?.phase?.programme;
      if (!programme) return;
      if (teacherProgrammeIdSet && !teacherProgrammeIdSet.has(programme.ID)) return;

      if (!progMap[programme.ID])
        progMap[programme.ID] = { programme, olas: [], phasesById: new Map() };

      if (!seenOlaIds.has(ola.ID)) {
        seenOlaIds.add(ola.ID);
        progMap[programme.ID].olas.push({ ola, pref: olaPrefMap[ola.ID], assigned: true });
      }

      const phaseId = a.phaseID ?? a.phase?.ID;
      if (phaseId && !progMap[programme.ID].phasesById.has(phaseId)) {
        const fullOla = fullOLAMap[ola.ID];
        const phaseEntry = (fullOla?.phases || ola.phases || []).find(
          p => (p.phaseID ?? p.phase?.ID) === phaseId
        );
        progMap[programme.ID].phasesById.set(phaseId, {
          phaseID: phaseId,
          phaseName: phaseEntry?.phase?.name ?? null,
          trajectoryName: phaseEntry?.phase?.trajectory?.name ?? null,
          campusName: phaseEntry?.phase?.campus?.name ?? null,
          phaseColor: phaseEntry?.phase?.color ?? null,
          pref: phasePrefMap[phaseId],
        });
      }
    });

    (olaPrefs || []).forEach(p => {
      if (seenOlaIds.has(p.olaId)) return;
      const ola = fullOLAMap[p.olaId];
      if (!ola) return;
      const programme = ola.phases?.[0]?.phase?.programme;
      if (!programme) return;
      if (teacherProgrammeIdSet && !teacherProgrammeIdSet.has(programme.ID)) return;

      if (!progMap[programme.ID])
        progMap[programme.ID] = { programme, olas: [], phasesById: new Map() };

      seenOlaIds.add(ola.ID);
      progMap[programme.ID].olas.push({ ola, pref: olaPrefMap[ola.ID], assigned: false });
    });

    const phaseInfoMap = {};
    Object.values(fullOLAMap).forEach(ola => {
      (ola.phases || []).forEach(op => {
        const pid = op.phaseID ?? op.phase?.ID;
        if (!pid || phaseInfoMap[pid]) return;
        const programme = op.phase?.programme;
        if (!programme) return;
        phaseInfoMap[pid] = {
          programmeId: programme.ID,
          programme,
          phaseName: op.phase?.name ?? null,
          trajectoryName: op.phase?.trajectory?.name ?? null,
          campusName: op.phase?.campus?.name ?? null,
          phaseColor: op.phase?.color ?? null,
        };
      });
    });

    (phasePrefs || []).forEach(p => {
      const phaseId = p.phaseId;
      const info = phaseInfoMap[phaseId];
      if (!info) return;
      const { programme } = info;
      if (!progMap[programme.ID]) return;
      if (progMap[programme.ID].phasesById.has(phaseId)) return;
      progMap[programme.ID].phasesById.set(phaseId, {
        phaseID: phaseId,
        phaseName: info.phaseName,
        trajectoryName: info.trajectoryName,
        campusName: info.campusName,
        phaseColor: info.phaseColor,
        pref: phasePrefMap[phaseId],
      });
    });

    const sortedProgrammes = Object.values(progMap)
      .map(({ programme }) => programme)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "nl"));

    const olasByProgLocal = {};
    const phasesByProgLocal = {};
    Object.values(progMap).forEach(({ programme, olas, phasesById }) => {
      olasByProgLocal[programme.ID] = olas.sort((a, b) =>
        (a.ola.name || "").localeCompare(b.ola.name || "", "nl")
      );
      phasesByProgLocal[programme.ID] = Array.from(phasesById.values()).sort((a, b) => {
        const trajCmp = (a.trajectoryName || "").localeCompare(b.trajectoryName || "", "nl");
        if (trajCmp !== 0) return trajCmp;
        return (a.phaseName || "").localeCompare(b.phaseName || "", "nl");
      });
    });

    const assignedTaskIds = new Set(
      (taskAssignments || []).map(ta => ta.task?.ID ?? ta.taskID).filter(Boolean)
    );

    const currentTasks = (taskAssignments || [])
      .map(ta => ta.task)
      .filter(Boolean)
      .filter((t, i, arr) => arr.findIndex(x => x.ID === t.ID) === i)
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "nl"))
      .map(task => ({ task, pref: taskPrefMap[task.ID] }));

    const teacherProgrammeIds = new Set(sortedProgrammes.map(p => p.ID));
    const newTasks = allTasks
      .filter(t =>
        !assignedTaskIds.has(t.ID) &&
        t.isTeacherSelectable === true &&
        (t.programmes || []).some(p => teacherProgrammeIds.has(p.ID ?? p.programmeID))
      )
      .map(t => ({ task: t, pref: taskPrefMap[t.ID] }))
      .filter(({ pref }) => pref?.wantsToDo === true)
      .sort((a, b) => (a.task.name || "").localeCompare(b.task.name || "", "nl"));

    setProgrammes(sortedProgrammes);
    setActiveTab(sortedProgrammes.length > 0 ? sortedProgrammes[0].ID : "taken");
    setOlasByProgramme(olasByProgLocal);
    setPhasesByProgramme(phasesByProgLocal);
    setCurrentTaskPrefs(currentTasks);
    setNewTaskPrefs(newTasks);
  };

  const loadTeacher = async (collegaId) => {
    setLoading(true);
    setOlasByProgramme({});
    setPhasesByProgramme({});
    setCurrentTaskPrefs([]);
    setNewTaskPrefs([]);
    setAllSubmissions([]);
    setSelectedSubmissionId(null);

    try {
      const [assignments, taskAssignments, subsResult, allOLAsFull, collega] = await Promise.all([
        api.getAssignmentsByCollegaId(collegaId).catch(() => []),
        api.getTaskAssignmentsByCollegaId(collegaId).catch(() => []),
        api.getHoewistSubmissionsByCollegaId(collegaId).catch(() => []),
        api.getOLAs().catch(() => []),
        api.getCollega(collegaId).catch(() => null),
      ]);

      const teacherProgrammeIdSet = new Set(
        (collega?.programmes || []).map(cp => cp.programmeID).filter(Boolean)
      );

      const fullOLAMap = {};
      (allOLAsFull || []).forEach(ola => { fullOLAMap[ola.ID] = ola; });

      const subs = Array.isArray(subsResult) ? subsResult : [];
      setAllSubmissions(subs);

      const currentYear = academicYears.find(y => y.current_year);
      const sub = subs.find(s => s.academicYearId === currentYear?.id)
        ?? (subs.length > 0 ? subs[0] : null);

      if (sub) setSelectedSubmissionId(sub.id);

      const [olaPrefs, taskPrefs, phasePrefs] = sub
        ? await Promise.all([
            api.getOlaPreferencesBySubmissionId(sub.id).catch(() => []),
            api.getTaskPreferencesBySubmissionId(sub.id).catch(() => []),
            api.getPhasePreferencesBySubmissionId(sub.id).catch(() => []),
          ])
        : [[], [], []];

      applyPreferences(sub, assignments, taskAssignments, fullOLAMap, olaPrefs, taskPrefs, phasePrefs, teacherProgrammeIdSet);

      teacherDataRef.current = { assignments, taskAssignments, fullOLAMap, teacherProgrammeIdSet };

    } catch (error) {
      toastService.send({ title: "Fout bij laden", message: error.toString() });
    }
    setLoading(false);
  };

  const switchYear = async (subId) => {
    const targetSub = allSubmissions.find(s => s.id === parseInt(subId, 10));
    if (!targetSub) return;

    setLoading(true);
    setSelectedSubmissionId(targetSub.id);

    try {
      const { assignments, taskAssignments, fullOLAMap, teacherProgrammeIdSet } = teacherDataRef.current;
      const [olaPrefs, taskPrefs, phasePrefs] = await Promise.all([
        api.getOlaPreferencesBySubmissionId(targetSub.id).catch(() => []),
        api.getTaskPreferencesBySubmissionId(targetSub.id).catch(() => []),
        api.getPhasePreferencesBySubmissionId(targetSub.id).catch(() => []),
      ]);
      applyPreferences(targetSub, assignments, taskAssignments, fullOLAMap, olaPrefs, taskPrefs, phasePrefs, teacherProgrammeIdSet);
    } catch (error) {
      toastService.send({ title: "Fout bij laden", message: error.toString() });
    }
    setLoading(false);
  };

  const handleCollegaChange = e => {
    const id = parseInt(e.target.value, 10);
    setSelectedId(id > 0 ? id : null);
    if (id > 0) loadTeacher(id);
  };

  const olasForTab = useMemo(() =>
    olasByProgramme[activeTab] || [],
    [olasByProgramme, activeTab]
  );

  const phasesForTab = useMemo(() =>
    phasesByProgramme[activeTab] || [],
    [phasesByProgramme, activeTab]
  );

  if (initialLoading) {
    return (
      <Container className="pt-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="pt-3 pb-5">
      <h4 className="mb-3">Hoewist-bevragingsresultaten</h4>
      <Row>
        <Form.Group className="mb-3">
          <Form.Select onChange={handleCollegaChange} value={selectedId ?? -1}>
            <option value={-1}>Selecteer een docent...</option>
            {collegas.map(c => (
              <option key={c.ID} value={c.ID}>
                {c.firstName} {c.lastName}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Row>

      {selectedId && allSubmissions.length > 1 && (
        <Row className="mb-3">
          <Form.Group className="d-flex align-items-center gap-2">
            <Form.Label className="mb-0 text-nowrap">Academiejaar:</Form.Label>
            <Form.Select
              value={selectedSubmissionId ?? ""}
              onChange={e => switchYear(e.target.value)}
              style={{ width: "auto" }}
            >
              {[...allSubmissions]
                .sort((a, b) => {
                  const yearA = academicYears.find(y => y.id === a.academicYearId)?.year ?? "";
                  const yearB = academicYears.find(y => y.id === b.academicYearId)?.year ?? "";
                  return yearB.localeCompare(yearA);
                })
                .map(sub => {
                  const year = academicYears.find(y => y.id === sub.academicYearId);
                  return year ? (
                    <option key={sub.id} value={sub.id}>
                      {year.year}{year.current_year ? " (huidig)" : ""}
                    </option>
                  ) : null;
                })}
            </Form.Select>
          </Form.Group>
        </Row>
      )}

      {selectedId && loading && (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      )}

      {selectedId && !loading && allSubmissions.length === 0 && (
        <Alert variant="warning">Deze docent heeft geen hoewist-inzending.</Alert>
      )}

      {selectedId && !loading && allSubmissions.length > 0 && (
        <>
          <Nav
            variant="tabs"
            activeKey={String(activeTab)}
            onSelect={k => setActiveTab(k === "taken" ? "taken" : parseInt(k, 10))}
            className="mb-3"
          >
            {programmes.map(p => (
              <Nav.Item key={p.ID}>
                <Nav.Link eventKey={String(p.ID)}>{p.name}</Nav.Link>
              </Nav.Item>
            ))}
            <Nav.Item>
              <Nav.Link eventKey="taken">Taken</Nav.Link>
            </Nav.Item>
          </Nav>

          {!isTaskTab && (
            <>
              <h5 className="mb-2">Huidige vakken</h5>
              {(() => {
                const currentOlas = olasForTab.filter(e => e.assigned);
                const newOlas = olasForTab.filter(e => !e.assigned);
                return (
                  <>
                    {currentOlas.length === 0
                      ? <Alert variant="info" className="mb-4">Geen OLA's toegewezen.</Alert>
                      : <OlaTable items={currentOlas} scoreVariant={scoreVariant} />
                    }
                    {newOlas.length > 0 && (
                      <>
                        <h5 className="mb-2">Niet-toegewezen vakken</h5>
                        <OlaTable items={newOlas} scoreVariant={scoreVariant} />
                      </>
                    )}
                  </>
                );
              })()}

              {phasesForTab.length > 0 && (
                <>
                  <h5 className="mb-2">Fasenvoorkeuren</h5>
                  <PhaseTable phases={phasesForTab} scoreVariant={scoreVariant} />
                </>
              )}
            </>
          )}

          {isTaskTab && (
            <TaskTables currentTaskPrefs={currentTaskPrefs} newTaskPrefs={newTaskPrefs} />
          )}
        </>
      )}
    </Container>
  );
}

export default HoewistResults;
